import { SplitBox } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AiChatLine,
  EditPictogram,
  SearchPictogram,
  UpsellFill,
} from "@dropbox/dig-icons/assets";
import { Link } from "@mirage/link/Link";
import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import { useDashTrialInfo } from "@mirage/service-dash-trial/hooks/useDashTrialInfo";
import { useNotificationsLastSeen } from "@mirage/service-dash-trial/hooks/useNotificationsLastSeen";
import { useDoOnceOnMount } from "@mirage/shared/hooks/useDoOnceOnMount";
import i18n from "@mirage/translations";
import { useWindowSize } from "@react-hookz/web";
import {
  ComponentType,
  SVGAttributes,
  useEffect,
  useMemo,
  useState,
} from "react";
import { InfoModal } from "../Modals/InfoModal";
import {
  useLogSelectUpsellComponent,
  useLogShownUpsellComponent,
} from "../TrialEndingNotification/utils";
import styles from "./TrialEndingModal.module.css";

// this same video is used by the welcome modal
const HERO_VIDEO =
  "https://assets.dropbox.com/images/dashweb/onboarding/holistic-ai-ui.webm";

// This text is subject to change, so for now aligning with the same text as the welcome modal
const FEATURES: [ComponentType<SVGAttributes<SVGElement>>, string][] = [
  [SearchPictogram, i18n.t("welcome_search_blurb")],
  [AiChatLine, i18n.t("welcome_ask_blurb")],
  [EditPictogram, i18n.t("welcome_write_blurb")],
];

const VERTICAL_BREAKPOINT = 1023;
const TRIGGER_TRIAL_ENDING_MODAL_AFTER_DAYS_LEFT = 14;
const TRIAL_ENDING_MODAL_DAY_COOLDOWN = 7; // we only want to show the modal once every 7 days

function shouldShowTrialEndingModal({
  dashTrialActivated,
  daysRemaining,
  lastSeen,
}: {
  dashTrialActivated: boolean;
  daysRemaining: number;
  lastSeen: number;
}): boolean {
  return (
    dashTrialActivated &&
    daysRemaining > 0 &&
    daysRemaining <= TRIGGER_TRIAL_ENDING_MODAL_AFTER_DAYS_LEFT &&
    daysRemaining <= lastSeen - TRIAL_ENDING_MODAL_DAY_COOLDOWN
  );
}

export const TrialEndingModalWrapper = () => {
  const { daysRemainingInDashTrial, dashTrialActivated } = useDashTrialInfo();
  const { lastSeenTrialEndingModal } = useNotificationsLastSeen();
  const [shouldRenderModal, setShouldRenderModal] = useState(false);

  const shouldShow = shouldShowTrialEndingModal({
    dashTrialActivated,
    daysRemaining: daysRemainingInDashTrial,
    lastSeen: lastSeenTrialEndingModal ?? 0,
  });

  // Since the modal sets the value of lastSeenTrialEndingModal, shouldShow goes
  // true and then immediately switches back to false and would remove the modal
  // from the DOM unless we use shouldRenderModal in place of shouldShow.
  useEffect(() => {
    if (shouldShow && !shouldRenderModal) {
      setShouldRenderModal(true);
    }
  }, [shouldShow, shouldRenderModal]);

  return (
    <>
      {shouldRenderModal && (
        <TrialEndingModal daysLeft={daysRemainingInDashTrial} />
      )}
    </>
  );
};

export const TrialEndingModal = ({ daysLeft }: { daysLeft: number }) => {
  const [open, setOpen] = useState(true);
  const isAdmin = useIsAdminUser();
  const windowWidth = useWindowSize().width;
  const vertical = windowWidth <= VERTICAL_BREAKPOINT;
  const { adminName, adminEmail } = useDashTrialInfo();
  const href = "https://dash.dropbox.com/plans";
  const { setLastSeen } = useNotificationsLastSeen();
  const logShownUpsell = useLogShownUpsellComponent();
  const logSelectUpsell = useLogSelectUpsellComponent();

  const handleViewPlans = () => {
    setOpen(false);
    logSelectUpsell({
      actionSurface: "modal",
      ctaName: isAdmin ? "view_plans" : "ask_your_admin",
    });
  };

  const leftContent = useMemo(
    () => (
      <Box
        className={styles.infoContentContainer}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          className={vertical ? styles.infoContentVertical : styles.infoContent}
          display="flex"
          flexDirection="column"
        >
          <Title
            weightVariant="emphasized"
            size={vertical ? "medium" : "large"}
          >
            {i18n.t("trial_ending_modal_title", {
              count: daysLeft,
            })}
          </Title>
          <Text
            size={vertical ? "medium" : "large"}
            className={styles.valueProp}
          >
            {i18n.t("trial_ending_modal_value_prop")}
          </Text>
          <Box display="flex" flexDirection="column" rowGap="Micro Large">
            {FEATURES.map(([Icon, description]) => (
              <Box
                display="flex"
                flexDirection="row"
                key={description}
                columnGap="Micro Small"
              >
                <UIIcon src={Icon} />
                <Text size={vertical ? "medium" : "large"}>{description}</Text>
              </Box>
            ))}
          </Box>
          <Box
            className={vertical ? styles.ctaSectionVertical : styles.ctaSection}
          >
            {isAdmin ? (
              <Link
                variant="monochromatic"
                hasNoUnderline
                href={href}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  className={styles.actionButton}
                  variant="primary"
                  size={vertical ? "medium" : "large"}
                  onClick={handleViewPlans}
                >
                  <UIIcon src={UpsellFill} size="medium" />
                  {i18n.t("trial_ending_modal_cta")}
                </Button>
              </Link>
            ) : (
              <>
                <Text
                  size={vertical ? "medium" : "large"}
                  isBold
                  style={{ marginRight: "var(--spacing__unit--0_5)" }}
                >
                  {i18n.t("trial_ending_modal_contact_admin_cta_prefix")}
                </Text>
                <Text size={vertical ? "medium" : "large"}>
                  {i18n.t("trial_ending_modal_contact_admin_cta_message", {
                    admin_name: adminName,
                    admin_email: adminEmail,
                  })}
                </Text>
              </>
            )}
          </Box>
        </Box>
      </Box>
    ),
    [daysLeft, isAdmin, adminName, adminEmail, vertical],
  );

  useDoOnceOnMount(() => {
    setLastSeen({ modal: daysLeft });
    logShownUpsell({
      upsellFormat: "modal",
      ctaName: isAdmin ? "view_plans" : "ask_your_admin",
    });
  });

  const nintyPercentWidth = Math.min(windowWidth * 0.9, 550);

  return (
    <InfoModal
      open={open}
      rootClassName={styles.trialEndingModal}
      bodyClassName={
        vertical
          ? styles.trialEndingModalBodyVertical
          : styles.trialEndingModalBody
      }
      width={vertical ? nintyPercentWidth : 1062}
      onClose={() => setOpen(false)}
      withClose
      body={
        vertical ? (
          <div
            className={styles.verticalWrapper}
            data-testid="trial-ending-modal"
          >
            <Box
              className={styles.heroPane}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <video style={{ maxWidth: "100%" }} autoPlay muted loop>
                <source src={HERO_VIDEO} type="video/webm" />
              </video>
            </Box>
            {leftContent}
          </div>
        ) : (
          <SplitBox
            height="100%"
            leftContent={leftContent}
            rightContent={
              <Box
                className={styles.heroPane}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <video autoPlay muted loop>
                  <source src={HERO_VIDEO} type="video/webm" />
                </video>
              </Box>
            }
          />
        )
      }
    />
  );
};
